import { useCallback } from 'react'
import { computeImpactTCChange, createTechnicalColumn } from 'src/endpoints'

import { useApiRequest } from '@/endpoints/hooks'
import { TechnicalColumnDto } from '@/endpoints/models'
import { AsyncTechnicalColumnUpdateType } from '@/enums'
import { useAppContext, useUnwrappedTrigger } from '@/hooks'
import { useTaskMonitor } from '@/hooks/useTaskMonitor'
import { useConfirmationModal } from '@/pages/App/components/ConfirmationService'
import { castCreateUsingPostBody, useCreateUsingPostMutation } from '@/rtkApi'
import {
	CreateUsingPostApiArg,
	CreateUsingPostApiResponse,
} from '@/rtkApi/mddApiSliceGenerated'
import { logErrorAndNotifyWithToast } from '@/utils/errorHandling'

import { PostProcessTechnicalColumnDto } from '../../types'
import { updateAndProcessTechnicalColumns } from '../../utils'

export const useConfirmationTechnicalColumn = () => {
	const { t } = useAppContext()
	const { openConfirmModal, closeConfirmModal } = useConfirmationModal()
	const request = useApiRequest()
	const { monitorTask } = useTaskMonitor()

	const [asyncTechnicalColumnUpdateRaw] = useCreateUsingPostMutation()
	const asyncTechnicalColumnUpdate = useUnwrappedTrigger<
		CreateUsingPostApiResponse,
		CreateUsingPostApiArg
	>(asyncTechnicalColumnUpdateRaw)

	const handleUpdate = useCallback(
		async (
			technicalColumn: TechnicalColumnDto,
			postProcessTechnicalColumnDto: PostProcessTechnicalColumnDto,
		) => {
			try {
				const impact = await request(
					computeImpactTCChange(technicalColumn?.id || 0, technicalColumn),
				)

				if (impact.data?.affectedElements?.TABLE) {
					const description = t('TECHNICAL_COLUMN_UPDATE_AFFECTED_MESSAGE', [
						impact.data?.affectedElements?.TABLE || 0,
					])

					openConfirmModal({
						catchOnCancel: true,
						title: t('TECHNICAL_COLUMN_UPDATE_CONFIRM_TITLE'),
						description,
						isAsync: true,
					})
						.then((options) => {
							if (options?.isCanceled) {
								postProcessTechnicalColumnDto(technicalColumn)
								return
							}

							updateAndProcessTechnicalColumns({
								technicalColumn,
								postProcessTechnicalColumnDto,
								asyncTechnicalColumnUpdate,
								monitorTask,
								closeConfirmModal,
								t,
							})
						})
						.catch((error) => {
							logErrorAndNotifyWithToast({
								error,
								context: {
									operation: 'technicalColumn.update.confirm',
									details: { technicalColumnId: technicalColumn?.id },
								},
								t,
							})
						})
				} else {
					const result = await asyncTechnicalColumnUpdate({
						body: castCreateUsingPostBody(technicalColumn),
						type: AsyncTechnicalColumnUpdateType.UPDATE_TECHNICAL_COLUMNS,
					})
					postProcessTechnicalColumnDto(result)
				}
			} catch (error) {
				logErrorAndNotifyWithToast({
					error,
					context: {
						operation: 'technicalColumn.update',
						details: { technicalColumnId: technicalColumn?.id },
					},
					t,
				})
				postProcessTechnicalColumnDto(null)
			}
		},
		[
			request,
			t,
			openConfirmModal,
			asyncTechnicalColumnUpdate,
			closeConfirmModal,
			monitorTask,
		],
	)

	const handleCreate = useCallback(
		async (
			technicalColumn: TechnicalColumnDto,
			postProcessTechnicalColumnDto: PostProcessTechnicalColumnDto,
		) => {
			try {
				if (
					technicalColumn.stereotypeIds &&
					technicalColumn.stereotypeIds?.length > 0
				) {
					const description = <div>{t('TECHNICAL_COLUMN_ADD_TO_TABLES')}</div>

					openConfirmModal({
						catchOnCancel: true,
						title: t('TECHNICAL_COLUMN_UPDATE_CONFIRM_TITLE'),
						description,
						isAsync: true,
					})
						.then(async (options) => {
							if (options?.isCanceled) {
								postProcessTechnicalColumnDto(technicalColumn)
								return
							}

							try {
								const value = await request(
									createTechnicalColumn(technicalColumn, {
										updateElements: true,
									}),
								)
								postProcessTechnicalColumnDto(value.data)
							} catch (error) {
								logErrorAndNotifyWithToast({
									error,
									context: {
										operation: 'technicalColumn.create.withUpdate',
										details: { technicalColumn },
									},
									t,
								})
								try {
									const fallbackValue = await request(
										createTechnicalColumn(technicalColumn, {
											updateElements: false,
										}),
									)
									postProcessTechnicalColumnDto(fallbackValue.data)
								} catch (fallbackError) {
									logErrorAndNotifyWithToast({
										error: fallbackError,
										context: {
											operation: 'technicalColumn.create.withoutUpdate',
											details: { technicalColumn },
										},
										t,
									})
									postProcessTechnicalColumnDto(null)
								}
							}
						})
						.catch(async (confirmError) => {
							logErrorAndNotifyWithToast({
								error: confirmError,
								context: {
									operation: 'technicalColumn.create.cancelled',
									details: { technicalColumn },
								},
								t,
							})
							try {
								const value = await request(
									createTechnicalColumn(technicalColumn, {
										updateElements: false,
									}),
								)
								postProcessTechnicalColumnDto(value.data)
							} catch (fallbackError) {
								logErrorAndNotifyWithToast({
									error: fallbackError,
									context: {
										operation: 'technicalColumn.create.fallback',
										details: { technicalColumn },
									},
									t,
								})
								postProcessTechnicalColumnDto(null)
							}
						})
				} else {
					const value = await request(
						createTechnicalColumn(technicalColumn, {
							updateElements: false,
						}),
					)
					postProcessTechnicalColumnDto(value.data)
				}
			} catch (error) {
				logErrorAndNotifyWithToast({
					error,
					context: {
						operation: 'technicalColumn.create',
						details: { technicalColumn },
					},
					t,
				})
				postProcessTechnicalColumnDto(null)
			}
		},
		[openConfirmModal, request, t],
	)

	const onConfirmationTechnicalColumn = useCallback(
		async (
			technicalColumn: TechnicalColumnDto,
			postProcess: PostProcessTechnicalColumnDto,
		) => {
			if (technicalColumn?.id) {
				await handleUpdate(technicalColumn, postProcess)
			} else {
				await handleCreate(technicalColumn, postProcess)
			}
		},
		[handleCreate, handleUpdate],
	)

	return { onConfirmationTechnicalColumn }
}
